import { AxiosResponse, AxiosStatic } from "axios";
import { IAliasElem, IUserAccount, IUserInfo } from "../../App/types/common";
import { modifyObjectDeletedPatch, patchPayloadCreator } from "../../components/course-author/helpers/patches/creators";

import { IPatchPayload } from "../../components/course-author/helpers/patches";
import {
  addAccountEmailPatch,
  modifyAccountEmailPatch,
  deleteAccountEmailPatch,
  undeleteAccountEmailPatch,
  modifyAccountTitlePatch,
  addAccountAliasPatch,
  modifyAccountAliasPatch,
  deleteAccountAliasPatch,
  undeleteAccountAliasPatch,
  modifyPreferredLocalePatch,
  insertClientFeaturePatch,
  deleteClientFeaturePatch,
  modifyOwningOrgPatch,
} from "../../App/AccountManagement/patches";
import { UserAccountPatchResponse, UserIdResponse, EmailAddressTag } from "../../App/AccountManagement/types";
import { AxiosInterceptedResult } from "../interceptors";
import { IResolveResponse } from "../ResolveApi/resolve-api.types";
import { IUserAccountInfoResponse, IUserAccountResponse } from "../../parsers/resolve";
import { PreferredLocale } from "../../utils/locales";
import { ClientFeature } from "../../parsers/login";
import { parseUserAccountResponse, getCurrentAliases } from "./helpers";

export interface IUsersApiDependencies {
  axios: AxiosStatic;
  saltcomplianceApiRoot: string;
}

interface IAccountsResponse<T> {
  value: string;
  bindings: { [accountId: string]: T };
}

export interface IUsersApi {
  getAllForClass: (accountId: string, courseId: string, classId: string) => Promise<AxiosResponse>;
  getAllForScormPackage: (accountId: string, courseId: string, classId: string) => Promise<AxiosResponse>;
  getEditableByRelativeTo: (accountId: string, organizationId: string) => Promise<UserIdResponse>;
  getMembersByScormPackage: (accountId: string, scormPackageId: string) => Promise<AxiosResponse>;
  patchAccount: (activeUser: string, accountId: string, patch: IPatchPayload) => Promise<UserAccountPatchResponse>;
  getUsersInfo: (activeUserId: string, usersIds: string[]) => Promise<any>;
  getUserById: (activeUserId: string, userId: string) => Promise<IUserAccount>;
  deleteUser: (currentAccountId: string, accountId: string) => Promise<UserAccountPatchResponse>;
  addNewUser: (accountId: string, currentAccountId: string) => Promise<AxiosResponse>;
  modifyTitle: (activeUserId: string, userId: string, title: string) => Promise<UserAccountPatchResponse>;
  addEmailAddress: (activeUserId: string, userId: string, email: string) => Promise<UserAccountPatchResponse>;
  modifyEmailAddress: (activeUserId: string, userId: string, newEmail: string) => Promise<UserAccountPatchResponse>;
  deleteEmailAddress: (activeUserId: string, userId: string) => Promise<UserAccountPatchResponse>;
  undeleteEmailAddress: (activeUserId: string, userId: string) => Promise<UserAccountPatchResponse>;
  addAccountAlias: (
    organizationId: string,
    activeUserId: string,
    userId: string,
    alias: string
  ) => Promise<UserAccountPatchResponse>;
  modifyAccountAlias: (
    organizationId: string,
    activeUserId: string,
    userId: string,
    alias: string
  ) => Promise<UserAccountPatchResponse>;
  deleteAccountAlias: (activeUserId: string, userId: string) => Promise<UserAccountPatchResponse>;
  undeleteAccountAlias: (activeUserId: string, userId: string) => Promise<UserAccountPatchResponse>;
  modifyPassword: (activeUserId: string, userId: string, newPassword: string) => Promise<AxiosResponse>;
  modifyPreferredLocale: (
    activeUserId: string,
    userId: string,
    newLocale: PreferredLocale | null
  ) => Promise<UserAccountPatchResponse>;
  insertClientFeature: (
    activeUserId: string,
    userId: string,
    newClientFeature: ClientFeature
  ) => Promise<UserAccountPatchResponse>;
  deleteClientFeature: (
    activeUserId: string,
    userId: string,
    clientFeature: ClientFeature
  ) => Promise<UserAccountPatchResponse>;
  modifyOwningOrg: (activeUserId: string, userId: string, newOwningOrg: string) => Promise<UserAccountPatchResponse>;
  migrateAliasToNewOrg: (
    currentAccountId: string,
    accountId: string,
    oldOwningOrgId: string
  ) => Promise<UserAccountPatchResponse>;
}

export default class UsersApi implements IUsersApi {
  private readonly apiRoot: string;
  private readonly axios: AxiosStatic;

  constructor(dependencies: IUsersApiDependencies) {
    this.apiRoot = dependencies.saltcomplianceApiRoot;
    this.axios = dependencies.axios;
  }

  public getAllForClass = (accountId: string, courseId: string, classId: string) => {
    return this.axios.get(`${this.apiRoot}/analytics/byCourse/${courseId}/byClass/${classId}/accounts`, {
      params: { activeUser: accountId },
    });
  };

  public getAllForScormPackage = (accountId: string, courseId: string, scormPackageId: string) => {
    return this.axios.get(`${this.apiRoot}/analytics/byScormPackage/${scormPackageId}/accounts`, {
      params: { activeUser: accountId },
    });
  };

  public getEditableByRelativeTo = (accountId: string, organizationId: string): Promise<UserIdResponse> => {
    return this.axios.get(
      `${this.apiRoot}/collections/accounts/editableBy/${accountId}/relativeToOrg/${organizationId}`,
      {
        params: { activeUser: accountId },
      }
    );
  };

  public getMembersByScormPackage = (accountId: string, scormPackageId: string) => {
    return this.axios.get(`${this.apiRoot}/collections/accounts/byScormPackage/${scormPackageId}/members`, {
      params: { activeUser: accountId },
    });
  };

  public patchAccount = (
    activeUser: string,
    accountId: string,
    patch: IPatchPayload
  ): Promise<UserAccountPatchResponse> =>
    this.axios.post(`${this.apiRoot}/accounts/${accountId}/_patches`, patch, {
      params: { activeUser },
    });

  public getUsersInfo = async (accountId: string, usersIds: string[]) => {
    const userIds = usersIds.map((id: string) => `acc.${id}?accInfo`) as string[];

    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, userIds, {
      params: { activeUser: accountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountInfoResponse>>;

    if (err || !response) throw new Error("Error fetching users");

    return userIds.reduce(
      (acc: IUserInfo[], userId: string, i: number) =>
        response.data[i].code === 200
          ? [...acc, { accountId: userId.replace("accInfo", ""), ...response.data[i].value }]
          : acc,
      []
    );
  };

  public getUserById = async (activeUserId: string, userId: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [userId], {
      params: { activeUser: activeUserId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { state } = parseUserAccountResponse(response);

    return state as IUserAccount;
  };

  public deleteUser = async (currentAccountId: string, accountId: string) => {
    const response = await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    });

    if (response[0]) throw new Error("Error fetching user");

    const versionId = response[1].data[0].value.versionId;
    const deletePatch = modifyObjectDeletedPatch(versionId, currentAccountId);
    const patchPayload = patchPayloadCreator(versionId, deletePatch);
    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public addNewUser = async (currentAccountId: string, organizationId: string) => {
    const userPayload = {
      aliases: {
        elems: [],
        nextId: 0,
      },
      deleted: false,
      displayName: "New Account",
      emailAddresses: {
        elems: [],
        nextId: 0,
      },
      oldClientFeatures: ["learner"],
      owner: organizationId,
      preferredLocale: null,
    };

    const [err, response] = (await this.axios.post(`${this.apiRoot}/accounts`, userPayload, {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IAccountsResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    return response;
  };

  public modifyTitle = async (currentAccountId: string, accountId: string, title: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const modifyPatch = modifyAccountTitlePatch(versionId, state, currentAccountId, title);

    const patchPayload = patchPayloadCreator(versionId, modifyPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public addEmailAddress = async (currentAccountId: string, accountId: string, email: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId } = parseUserAccountResponse(response);

    const addPatch = addAccountEmailPatch(versionId, currentAccountId, email);

    const patchPayload = patchPayloadCreator(versionId, addPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public modifyEmailAddress = async (currentAccountId: string, accountId: string, newEmail: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentEmail = state.emailAddresses.elems[0].state.address;

    const currentEmailTag = state.emailAddresses.elems[0].state.tag as EmailAddressTag;

    const modifyPatch = modifyAccountEmailPatch(versionId, currentAccountId, currentEmail, currentEmailTag, newEmail);

    const patchPayload = patchPayloadCreator(versionId, modifyPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public deleteEmailAddress = async (currentAccountId: string, accountId: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId } = parseUserAccountResponse(response);

    const deletePatch = deleteAccountEmailPatch(versionId, currentAccountId);

    const patchPayload = patchPayloadCreator(versionId, deletePatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public undeleteEmailAddress = async (currentAccountId: string, accountId: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId } = parseUserAccountResponse(response);

    const undeletePatch = undeleteAccountEmailPatch(versionId, currentAccountId);

    const patchPayload = patchPayloadCreator(versionId, undeletePatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public addAccountAlias = async (
    organizationId: string,
    currentAccountId: string,
    accountId: string,
    alias: string
  ) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentAliasElem = getCurrentAliases(state);

    const addPatch = addAccountAliasPatch(
      versionId,
      currentAccountId,
      organizationId,
      alias,
      currentAliasElem?.id ?? 0
    );

    const patchPayload = patchPayloadCreator(versionId, addPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public modifyAccountAlias = async (
    organizationId: string,
    currentAccountId: string,
    accountId: string,
    newAlias: string
  ) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentAliasElem = getCurrentAliases(state);

    if (!currentAliasElem) throw new Error("Error recovering current alias");

    const modifyPatch = modifyAccountAliasPatch(
      currentAliasElem.id,
      versionId,
      currentAccountId,
      organizationId,
      currentAliasElem.state,
      newAlias
    );

    const patchPayload = patchPayloadCreator(versionId, modifyPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public deleteAccountAlias = async (currentAccountId: string, accountId: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentAliasElem = getCurrentAliases(state);
    if (!currentAliasElem) throw new Error("Error recovering current alias");

    const deletePatch = deleteAccountAliasPatch(currentAliasElem.id, versionId, currentAccountId);
    const patchPayload = patchPayloadCreator(versionId, deletePatch);
    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public undeleteAccountAlias = async (currentAccountId: string, accountId: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentAliasElem = getCurrentAliases(state);
    if (!currentAliasElem) throw new Error("Error recovering current alias");

    const undeletePatch = undeleteAccountAliasPatch(currentAliasElem.id, versionId, currentAccountId);
    const patchPayload = patchPayloadCreator(versionId, undeletePatch);
    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public modifyPassword = async (currentAccountId: string, accountId: string, newPassword: string) => {
    const [err, response] = (await this.axios.put(`${this.apiRoot}/accounts/${accountId}/_password`, newPassword, {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<string>;

    if (err || !response) throw new Error("Error changing password");

    return response;
  };

  public modifyPreferredLocale = async (
    currentAccountId: string,
    accountId: string,
    newLocale: PreferredLocale | null
  ) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentLocale = state.preferredLocale;

    const modifyPatch = modifyPreferredLocalePatch(versionId, currentAccountId, currentLocale, newLocale);

    const patchPayload = patchPayloadCreator(versionId, modifyPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public insertClientFeature = async (currentAccountId: string, accountId: string, newClientFeature: ClientFeature) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId } = parseUserAccountResponse(response);

    const insertPatch = insertClientFeaturePatch(versionId, currentAccountId, newClientFeature);

    const patchPayload = patchPayloadCreator(versionId, insertPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public deleteClientFeature = async (currentAccountId: string, accountId: string, clientFeature: ClientFeature) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId } = parseUserAccountResponse(response);

    const deletePatch = deleteClientFeaturePatch(versionId, currentAccountId, clientFeature);

    const patchPayload = patchPayloadCreator(versionId, deletePatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public modifyOwningOrg = async (currentAccountId: string, accountId: string, newOwningOrg: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const currentOwningOrg = state.owner;

    const modifyPatch = modifyOwningOrgPatch(versionId, currentAccountId, currentOwningOrg, newOwningOrg);

    const patchPayload = patchPayloadCreator(versionId, modifyPatch);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };

  public migrateAliasToNewOrg = async (currentAccountId: string, accountId: string, oldOwningOrgId: string) => {
    const [err, response] = (await this.axios.post(`${this.apiRoot}/bulk/resolve`, [accountId], {
      params: { activeUser: currentAccountId },
    })) as AxiosInterceptedResult<IResolveResponse<IUserAccountResponse>>;

    if (err || !response) throw new Error("Error fetching user");

    const { versionId, state } = parseUserAccountResponse(response);

    const newOrganizationId = state.owner;

    const aliases = state.aliases.elems as IAliasElem[];

    const oldOwningOrgAlias = aliases.find(a => a.state.namespace === `org.${oldOwningOrgId}`) as IAliasElem;
    const newOwningOrgAlias = aliases.find(a => a.state.namespace === `org.${newOrganizationId}`);

    const deleteAliasPatch = deleteAccountAliasPatch(oldOwningOrgAlias.id, versionId, currentAccountId);
    const addAliasPatch = addAccountAliasPatch(
      versionId,
      currentAccountId,
      newOrganizationId,
      oldOwningOrgAlias.state.alias.name,
      oldOwningOrgAlias.id + 1
    );

    const patches = [];

    if (newOwningOrgAlias) {
      const undeleteAliasPatch = undeleteAccountAliasPatch(newOwningOrgAlias?.id, versionId, currentAccountId);
      const modifyAliasPatch = modifyAccountAliasPatch(
        newOwningOrgAlias?.id,
        versionId,
        currentAccountId,
        newOrganizationId,
        newOwningOrgAlias.state,
        oldOwningOrgAlias.state.alias.name
      );
      patches.push(deleteAliasPatch, undeleteAliasPatch, modifyAliasPatch);
    } else {
      oldOwningOrgAlias.deleted ? patches.push(addAliasPatch) : patches.push(deleteAliasPatch, addAliasPatch);
    }

    const patchPayload = patchPayloadCreator(versionId, patches);

    const userAccountId = accountId.replace("acc.", "").replace("?", "");

    return this.patchAccount(currentAccountId, userAccountId, patchPayload);
  };
}
